import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@vimaana/ui";

/**
 * Sperrzeiten der Inhaber — „Nicht da" im Planer (Benjamin, 16.10.2026).
 * Backend: hetzner/webhook/kontor_sperrzeit.py. Eine Sperre ist kein Termin:
 * keine Stunden, kein Lohn. Sie macht die Person für die Konfliktprüfung
 * belegt — wer sie trotzdem einplant, wird gefragt.
 *
 * Bürotag (20.10.2026, Migration 224): dieselbe Zeile mit `art: "buero"` — nur
 * Inhaber, kein Lohn, belegt wie „Nicht da", sieht im Planer aber anders aus.
 * Ein Bürotag für beide sind zwei Zeilen mit gleichem von/bis.
 */

export type Sperre = {
  /** Zahl bei eingetragenen, „abo:…" bei Sperren aus einem Kalender. */
  schluessel: string;
  id: number | null;
  person_id: string;
  person_name: string;
  /** Berliner Ortszeit ohne Zone („2026-10-17T14:00"); bis ist exklusiv. */
  von: string;
  bis: string;
  ganztags: boolean;
  notiz: string;
  /** kontor = eingetragen, änderbar · kalender = aus einem Abo mit „sperrt", nur lesen. */
  quelle: "kontor" | "kalender";
  kalender: string;
  /** abwesend = „Nicht da" · buero = Bürotag. */
  art: SperrArt;
};

export type SperrArt = "abwesend" | "buero";

export type SperrenDaten = { ich: string; inhaber: { id: string; name: string }[]; sperren: Sperre[] };

export type SperreEingabe = { person_id?: string; von: string; bis: string; ganztags: boolean; notiz: string; art?: SperrArt };

export type SperreGespeichert = {
  sperre: Sperre;
  /** Wo die Person in der Zeit schon eingeplant ist — Hinweis, kein Fehler. */
  eingeplant: { termin_id: string; bezeichnung: string; von: string; bis: string }[];
};

const key = ["kontor", "sperrzeiten"] as const;

export function useSperrzeiten(from: string, to: string) {
  return useQuery<SperrenDaten>({
    queryKey: [...key, from, to],
    queryFn: () => apiGet<SperrenDaten>(`/api/kontor/sperrzeiten?from=${from}&to=${to}`),
    staleTime: 60_000,
  });
}

function useNeuLaden() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: key });
}

export function useSperreAnlegen() {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: (body: SperreEingabe) => apiPost<SperreGespeichert>("/api/kontor/sperrzeiten", body),
    onSuccess: neu,
  });
}

export function useSperreAendern() {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: ({ id, ...body }: SperreEingabe & { id: number }) =>
      apiPost<SperreGespeichert>(`/api/kontor/sperrzeiten/${id}`, body),
    onSuccess: neu,
  });
}

export function useSperreAufheben() {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: (id: number) => apiPost<{ aufgehoben: boolean }>(`/api/kontor/sperrzeiten/${id}/aufheben`, {}),
    onSuccess: neu,
  });
}

const tagIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Letzter Tag, den die Sperre berührt — ein Ende um Mitternacht zählt nicht mehr zum Folgetag. */
export function letzterTag(s: Pick<Sperre, "von" | "bis">): string {
  let letzter = s.bis.slice(0, 10);
  if (s.bis > s.von && s.bis.slice(11, 16) === "00:00") {
    const d = new Date(`${letzter}T12:00:00`);
    d.setDate(d.getDate() - 1);
    letzter = tagIso(d);
  }
  return letzter < s.von.slice(0, 10) ? s.von.slice(0, 10) : letzter;
}

/** Sperren nach Tag („JJJJ-MM-TT"); mehrtägige stehen an jedem ihrer Tage. */
export function sperrenProTag(sperren: Sperre[]): Map<string, Sperre[]> {
  const m = new Map<string, Sperre[]>();
  for (const s of sperren) {
    const letzter = letzterTag(s);
    const d = new Date(`${s.von.slice(0, 10)}T12:00:00`);
    for (let i = 0; i < 370; i++) {
      const k = tagIso(d);
      if (k > letzter) break;
      const arr = m.get(k) ?? [];
      arr.push(s);
      m.set(k, arr);
      d.setDate(d.getDate() + 1);
    }
  }
  return m;
}

/** Deckt die Sperre den ganzen Tag? (ganztags, oder von vorher bis nachher) */
export function ganzerTag(s: Sperre, tag: string): boolean {
  return s.ganztags || (s.von <= `${tag}T00:00` && (s.bis >= `${tag}T23:59` || s.bis.slice(0, 10) > tag));
}

/** „ganztägig" · „14:00–16:00" · „ab 14:00" · „bis 12:00" — bezogen auf den Tag. */
export function zeitAmTag(s: Sperre, tag: string): string {
  if (ganzerTag(s, tag)) return "ganztägig";
  const ab = s.von.slice(0, 10) === tag ? s.von.slice(11, 16) : "";
  const bis = s.bis.slice(0, 10) === tag ? s.bis.slice(11, 16) : "";
  if (ab && bis) return `${ab}–${bis}`;
  return ab ? `ab ${ab}` : `bis ${bis}`;
}

/** Anfangsbuchstabe für den Streifen im Monatsraster. */
export function kuerzel(name: string): string {
  return (name.trim()[0] || "?").toUpperCase();
}

export const istBuero = (s: Pick<Sperre, "art">) => s.art === "buero";

/** Bürotage desselben Zeitraums — für beide eingetragen, als einer gezeigt und geändert. */
export function bueroGeschwister(s: Sperre, alle: Sperre[]): Sperre[] {
  return alle.filter((x) => istBuero(x) && x.quelle === "kontor" && x.von === s.von && x.bis === s.bis);
}

/** Bürotage eines Tages zusammengefasst: je Zeitraum eine Gruppe mit allen Namen. */
export function bueroGruppen(sperren: Sperre[]): Sperre[][] {
  const m = new Map<string, Sperre[]>();
  for (const s of sperren.filter(istBuero)) {
    const k = `${s.von}|${s.bis}`;
    m.set(k, [...(m.get(k) ?? []), s]);
  }
  return [...m.values()];
}

/** „Benjamin & Volker" — Vornamen einer Bürotag-Gruppe. */
export function namenListe(gruppe: Sperre[]): string {
  return gruppe.map((s) => vorname(s.person_name)).join(" & ");
}

/** Vorname reicht in einer Zeile („Volker nicht da"). */
export function vorname(name: string): string {
  return name.trim().split(/\s+/)[0] || "Jemand";
}
